import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { Calendar, ShieldCheck, Clock } from "lucide-react";

function fmtDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { weekday: "short", day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
}

export default function PatientDashboardPage() {
  const { user, loading } = useAuth();
  const [appts, setAppts] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "patient") return;
    api.get("/appointments/mine").then((r) => setAppts(r.data.items || [])).finally(() => setFetching(false));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/patient/login" replace />;
  if (user.role !== "patient") return <Navigate to="/" replace />;

  const upcoming = appts.filter((a) => new Date(a.slot_datetime) >= new Date() && a.status === "booked");
  const past = appts.filter((a) => !(new Date(a.slot_datetime) >= new Date() && a.status === "booked"));

  return (
    <section className="mx-auto max-w-[920px] px-4 lg:px-6 py-10">
      <Seo title="My visits" canonicalPath="/patient" />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold">My visits</h1>
          <p className="text-muted-foreground mt-1">Hi {user.name}, here are your upcoming OTP appointments.</p>
        </div>
        <Link to="/doctors/pune" className="btn-primary" data-testid="book-another">Book another visit</Link>
      </div>

      <h2 className="font-display text-lg font-semibold mt-10">Upcoming</h2>
      {fetching ? (
        <div className="h-24 rounded-xl bg-muted/50 animate-pulse mt-3" />
      ) : upcoming.length === 0 ? (
        <p className="text-muted-foreground mt-3 text-sm" data-testid="no-upcoming">No upcoming visits. Book one in a few taps.</p>
      ) : (
        <div className="mt-4 grid gap-4">
          {upcoming.map((a) => (
            <article key={a.id} className="rounded-xl border border-border p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`appt-${a.id}`}>
              <div>
                <p className="font-display font-semibold">{a.doctor_name}</p>
                <p className="text-sm text-muted-foreground">{a.clinic_name}</p>
                <p className="text-sm mt-1 inline-flex items-center gap-1.5"><Calendar size={14} className="text-primary" />{fmtDateTime(a.slot_datetime)}</p>
              </div>
              <div className="rounded-lg bg-accent text-[hsl(var(--ld-jade-fg))] px-4 py-3 flex items-center gap-3">
                <ShieldCheck size={22} />
                <div>
                  <p className="text-xs uppercase tracking-wide font-semibold">Queue OTP</p>
                  <p className="font-display text-2xl font-extrabold tabular tracking-widest">{a.queue_otp}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <>
          <h2 className="font-display text-lg font-semibold mt-10">Past visits</h2>
          <ul className="mt-3 divide-y divide-border rounded-xl border border-border">
            {past.map((a) => (
              <li key={a.id} className="px-5 py-4 flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold">{a.doctor_name}</p>
                  <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5"><Clock size={12} />{fmtDateTime(a.slot_datetime)}</p>
                </div>
                <span className="text-xs text-muted-foreground capitalize">{a.status}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
